import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../AuthProvider';
import './McpSetup.css';
import './McpConnect.css';

const NODE_URL = process.env.REACT_APP_NODE_URL;

const CONNECTOR_URL = 'https://backendapi.vestmap.com/mcp';
const MCPB_VERSION = '1.8.0';
const MCPB_FILE = `/vestmap-mcp-${MCPB_VERSION}.mcpb`;
const GUIDE = '/mcp-guide';
const STARTER_PROMPT = 'Median household income at 47 Highland Park Village, Dallas, TX 75205: the block group vs. the 1, 3 and 5-mile rings.';

const CopyButton = ({ label, text, id, copied, onCopy, disabled }) => (
  <button
    type="button"
    className={`copy${copied === id ? ' done' : ''}`}
    disabled={disabled}
    onClick={() => onCopy(text, id)}
  >
    {copied === id ? 'Copied ✓' : label}
  </button>
);

const McpSetup = () => {
  const { user, mongoUser, mongoUserId, isLoading, logout, openLogin, openSignup, openChangePlan, makeAuthenticatedRequest } = useAuth();

  const navigate = useNavigate();
  const mcpCallbackUrl = `${window.location.origin}/mcp`;

  const handleLogout = () => {
    logout();
    // Hard reload to /mcp so all state is fully reset
    window.location.href = '/mcp';
  };

  const handleLogin = () => {
    openLogin({ authenticationCallbackUrl: mcpCallbackUrl });
  };

  const handleSignup = () => {
    // Use Outseta embedded modal with MCP plan pre-selected
    if (window.Outseta) {
      window.Outseta.auth.open({
        widgetMode: 'register',
        planUid: 'EWBwelmr',
        planPaymentTerm: 'month',
        skipPlanOptions: true,
        authenticationCallbackUrl: mcpCallbackUrl,
      });
    }
  };

  const [apiKey, setApiKey] = useState('');
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);

  const isAuthenticated = !!user && !!mongoUserId;

  const fetchOrCreateApiKey = useCallback(async () => {
    if (!mongoUserId) return;
    setApiKeyLoading(true);
    try {
      const res = await makeAuthenticatedRequest(`${NODE_URL}/user_data/mcp-api-key`, {
        method: 'POST',
        body: JSON.stringify({ userId: mongoUserId }),
      });
      const data = await res.json();
      if (data.apiKey) setApiKey(data.apiKey);
    } catch (err) {
      console.error('Failed to fetch API key:', err);
    } finally {
      setApiKeyLoading(false);
    }
  }, [mongoUserId, makeAuthenticatedRequest]);

  const fetchAccountInfo = useCallback(async () => {
    if (!mongoUserId) return;
    try {
      const res = await makeAuthenticatedRequest(`${NODE_URL}/user_data/mcp-account?userId=${mongoUserId}`);
      const data = await res.json();
      setAccountInfo(data);
    } catch (err) {
      console.error('Failed to fetch account info:', err);
    }
  }, [mongoUserId, makeAuthenticatedRequest]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrCreateApiKey();
      fetchAccountInfo();
    }
  }, [isAuthenticated, fetchOrCreateApiKey, fetchAccountInfo]);

  const handleRegenerate = async () => {
    if (!window.confirm('This will invalidate your current API key. Any configured MCP servers will need to be updated. Continue?')) return;
    setApiKeyLoading(true);
    try {
      const res = await makeAuthenticatedRequest(`${NODE_URL}/user_data/mcp-api-key/regenerate`, {
        method: 'POST',
        body: JSON.stringify({ userId: mongoUserId }),
      });
      const data = await res.json();
      if (data.apiKey) setApiKey(data.apiKey);
    } catch (err) {
      console.error('Failed to regenerate API key:', err);
    } finally {
      setApiKeyLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const usedToolCalls = accountInfo?.mcpToolCalls ? (accountInfo.mcpToolCalls.total - accountInfo.mcpToolCalls.remaining) : 0;

  const claudeCodeCmd = `npx vestmap-mcp setup ${apiKey}`;
  const codexCmd = `codex mcp add vestmap --env VESTMAP_API_KEY=${apiKey} -- npx -y vestmap-mcp`;

  if (isLoading) {
    return (
      <div className="mcp-page">
        <div className="mcp-container">
          <div className="mcp-loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mcp-page">
      <div className="mcp-container mcp-container-wide">
        {/* Back to VestMap */}
        <a
          href="/run-reports"
          className="mcp-back-link"
        >
          &larr; Return back to VestMap
        </a>

        {/* Header */}
        <div className="mcp-header">
          <div className="mcp-logo">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="8" fill="#4FA490" />
              <path d="M8 16L14 22L24 10" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <h1>VestMap AI Integration</h1>
          </div>
          <p className="mcp-subtitle">
            On-demand location intelligence for real estate — powered by ESRI, AGS, FHFA, FEMA, and US Census data
          </p>
        </div>

        {!isAuthenticated ? (
          /* ───────── NOT LOGGED IN ───────── */
          <div className="mcp-auth-section">
            <div className="mcp-features">
              <h2>What you get</h2>
              <div className="mcp-feature-grid">
                <div className="mcp-feature-card">
                  <span className="mcp-feature-icon">&#x1f4ca;</span>
                  <h3>Full Property Reports</h3>
                  <p>Demographics, population, income, median rent, home values, crime indexes, schools, employment, and growth projections</p>
                </div>
                <div className="mcp-feature-card">
                  <span className="mcp-feature-icon">&#x1f30d;</span>
                  <h3>1000+ GIS Data Fields</h3>
                  <p>Query ESRI, AGS, FHFA HPI, FEMA NRI, and Census data at block, tract, ZIP, county, CBSA, or state level</p>
                </div>
                <div className="mcp-feature-card">
                  <span className="mcp-feature-icon">&#x1f916;</span>
                  <h3>AI-Powered Analysis</h3>
                  <p>Generate OMs, run site selection, underwrite properties, compare markets, and assess climate risk</p>
                </div>
                <div className="mcp-feature-card">
                  <span className="mcp-feature-icon">&#x1f4ac;</span>
                  <h3>Works with Your AI</h3>
                  <p>Claude Desktop, Claude Code, ChatGPT, Codex — ask follow-up questions, compare addresses, export data</p>
                </div>
              </div>
            </div>

            <div className="mcp-auth-buttons">
              <button className="mcp-btn mcp-btn-primary" onClick={handleSignup}>
                Create Account
              </button>
              <button className="mcp-btn mcp-btn-secondary" onClick={handleLogin}>
                Sign In
              </button>
            </div>

            <p className="mcp-auth-note">
              Already have a VestMap account? Sign in to get your API key.
            </p>
          </div>
        ) : (
          /* ───────── LOGGED IN ───────── */
          <div className="mcp-dashboard">
            {/* Welcome + Logout */}
            <div className="mcp-welcome-bar">
              <div className="mcp-welcome">
                <h2>
                  Welcome, {mongoUser?.firstname || user?.given_name || 'there'}
                </h2>
                <span className="mcp-account-plan">
                  {accountInfo?.plan || '...'}
                </span>
              </div>
              <button className="mcp-btn-logout" onClick={handleLogout}>
                Sign Out
              </button>
            </div>

            <div className="mcpx">
              {/* ── 1. Recommended: remote connector ── */}
              <section className="cx" aria-labelledby="cx-title">
                <div className="cx-head">
                  <span className="cx-tag">Recommended · Always up to date</span>
                  <h2 id="cx-title">Connect VestMap to Claude</h2>
                  <p>Add VestMap as a connector once. Every new VestMap release shows up in Claude automatically.</p>
                  <div className="urlbar">
                    <code>{CONNECTOR_URL}</code>
                    <CopyButton label="Copy URL" text={CONNECTOR_URL} id="url" copied={copied} onCopy={copyToClipboard} />
                  </div>
                </div>

                <ol className="cx-steps">
                  <li className="cx-step">
                    <div className="num">1</div>
                    <div>
                      <h3>Open Settings → Connectors</h3>
                      <p>In Claude (desktop app or claude.ai), click your name in the bottom-left, then <b>Settings</b> → <b>Connectors</b>. Click <b>+&nbsp;Add</b> → <b>Add custom connector</b>.</p>
                      <figure className="shot">
                        <img src={`${GUIDE}/1-connectors.webp`} width="1400" height="867" alt="Claude Settings with Connectors selected in the sidebar and the Add button at the top right" loading="lazy" />
                        <span className="ring" style={{ left: '.3%', top: '90.8%', width: '16%', height: '6.2%' }} />
                        <span className="ring" style={{ left: '84%', top: '6%', width: '8.3%', height: '6%' }} />
                        <span className="note" style={{ right: '7.6%', top: '14.5%' }}>Add → Add custom connector</span>
                      </figure>
                    </div>
                  </li>

                  <li className="cx-step">
                    <div className="num">2</div>
                    <div>
                      <h3>Name it VestMap, paste the URL</h3>
                      <p>Name: <span className="kbd">VestMap</span> &nbsp;URL: <span className="kbd">{CONNECTOR_URL}</span><br />Then click <b>Continue</b>.</p>
                      <figure className="shot narrow">
                        <img src={`${GUIDE}/2-add-custom-connector.webp`} width="972" height="809" alt="Add custom connector dialog with VestMap as the name and the VestMap URL filled in" loading="lazy" />
                        <span className="ring" style={{ left: '3.2%', top: '23.6%', width: '93.6%', height: '9.2%' }} />
                        <span className="ring" style={{ left: '3%', top: '39.9%', width: '94%', height: '9.7%' }} />
                        <span className="ring" style={{ left: '78.8%', top: '87%', width: '18%', height: '9.2%' }} />
                      </figure>
                    </div>
                  </li>

                  <li className="cx-step">
                    <div className="num">3</div>
                    <div>
                      <h3>Log in with your VestMap account</h3>
                      <p>A browser tab opens. Click <b>Continue connecting</b>, then log in with your VestMap email and password. No password yet? Click <b>Forgot password</b> to set one.</p>
                      <div className="pair">
                        <figure className="shot">
                          <img src={`${GUIDE}/3-continue-connecting.webp`} width="1020" height="538" alt="Claude asks to finish connecting; the Continue connecting button" loading="lazy" />
                          <span className="ring" style={{ left: '30.3%', top: '66.3%', width: '34.4%', height: '16%' }} />
                        </figure>
                        <figure className="shot">
                          <img src={`${GUIDE}/4-vestmap-login.webp`} width="877" height="909" alt="VestMap login page with email and password fields" loading="lazy" />
                          <span className="ring" style={{ left: '3.4%', top: '80.6%', width: '93.1%', height: '10.4%' }} />
                        </figure>
                      </div>
                    </div>
                  </li>

                  <li className="cx-step">
                    <div className="num">4</div>
                    <div>
                      <h3>Connected. Ask Claude about any address.</h3>
                      <p>Head back to Claude, start a new chat, and try this:</p>
                      <div className="pair">
                        <figure className="shot">
                          <img src={`${GUIDE}/5-connected.webp`} width="965" height="741" alt="Connected confirmation page" loading="lazy" />
                        </figure>
                        <div className="prompt">
                          <q>{STARTER_PROMPT}</q>
                          <CopyButton label="Copy prompt" text={STARTER_PROMPT} id="prompt" copied={copied} onCopy={copyToClipboard} />
                        </div>
                      </div>
                    </div>
                  </li>

                  <li className="cx-step">
                    <div className="num opt">+</div>
                    <div>
                      <h3>Optional: let Claude run VestMap without asking each time</h3>
                      <p>In <b>Settings</b> → <b>Connectors</b> → <b>VestMap</b>, set <b>Read-only tools</b> to <b>Always allow</b>.</p>
                      <figure className="shot">
                        <img src={`${GUIDE}/6-always-allow.webp`} width="1400" height="623" alt="VestMap connector tool permissions with Always allow in the dropdown" loading="lazy" />
                        <span className="ring" style={{ left: '77.8%', top: '49.4%', width: '21.4%', height: '10.4%' }} />
                      </figure>
                    </div>
                  </li>
                </ol>

                <div className="cx-foot">Add it once and it's there in the Claude desktop app, claude.ai and the Claude mobile app.</div>
              </section>

              {/* ── 2. Other ways to connect ── */}
              <section className="sec">
                <h2>Other ways to connect</h2>

                <div className="card">
                  <div className="card-top">
                    <h3>Claude Desktop download (.mcpb)</h3>
                    <span className="ver">Current release: v{MCPB_VERSION}</span>
                  </div>
                  <div className="heads-up">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E8B45A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>
                    <span><b>The download stays on the version you installed.</b> Each new VestMap release means downloading and installing it again. The connector above updates on its own.</span>
                  </div>
                  <ol className="mini-steps">
                    <li>
                      <div>Download the extension.<br />
                        <a className="btn-sm" href={MCPB_FILE} download>Download vestmap-mcp-{MCPB_VERSION}.mcpb</a>
                      </div>
                    </li>
                    <li><div>Double-click the <span className="kbd">.mcpb</span> file. Claude Desktop opens the installer.</div></li>
                    <li>
                      <div>When it asks for your API key, paste this one, then click <b>Save</b>.
                        {apiKeyLoading ? (
                          <div className="key-loading">Generating key...</div>
                        ) : apiKey ? (
                          <>
                            <div className="keybox">
                              <code>{apiKey}</code>
                              <CopyButton label="Copy key" text={apiKey} id="key" copied={copied} onCopy={copyToClipboard} />
                            </div>
                            <button type="button" className="regen" onClick={handleRegenerate}>Regenerate key</button>
                          </>
                        ) : (
                          <div className="key-loading">No key generated</div>
                        )}
                      </div>
                    </li>
                  </ol>
                </div>

                <div className="grid2">
                  <div className="card">
                    <h3>Claude Code</h3>
                    <p>Paste in your terminal, then start a new Claude Code session.</p>
                    <div className="cmd">
                      <code>npx vestmap-mcp setup <span className="key">{apiKey || 'YOUR_API_KEY'}</span></code>
                      <CopyButton label="Copy" text={claudeCodeCmd} id="claude-code" disabled={!apiKey} copied={copied} onCopy={copyToClipboard} />
                    </div>
                  </div>
                  <div className="card">
                    <h3>OpenAI Codex</h3>
                    <p>Paste in your terminal, then start a new Codex session.</p>
                    <div className="cmd">
                      <code>codex mcp add vestmap --env VESTMAP_API_KEY=<span className="key">{apiKey || 'YOUR_API_KEY'}</span> -- npx -y vestmap-mcp</code>
                      <CopyButton label="Copy" text={codexCmd} id="codex" disabled={!apiKey} copied={copied} onCopy={copyToClipboard} />
                    </div>
                  </div>
                </div>
              </section>

              {/* ── 3. Tool calls ── */}
              <section className="sec">
                <h2>Tool calls</h2>
                <div className="credits">
                  <div className="card">
                    <div className="lbl">Tool calls left</div>
                    <div className="big">
                      <b>{accountInfo?.mcpToolCalls?.remaining?.toLocaleString() ?? '...'}</b>{' '}
                      <span>/ {accountInfo?.mcpToolCalls?.total?.toLocaleString() ?? '...'}</span>
                    </div>
                    <div className="bar">
                      <i style={{ width: accountInfo?.mcpToolCalls?.total ? `${(accountInfo.mcpToolCalls.remaining / accountInfo.mcpToolCalls.total) * 100}%` : '0%' }} />
                    </div>
                    <p>{usedToolCalls.toLocaleString()} used this period</p>
                  </div>
                  <div className="card">
                    <div className="lbl">Need more?</div>
                    <p style={{ marginTop: 12 }}>Add 1,000 tool calls to your account for $49, one time.</p>
                    <button
                      type="button"
                      className="btn-sm"
                      style={{ marginTop: 14 }}
                      onClick={() => {
                        if (window.Outseta) {
                          window.Outseta.profile.open({ tab: 'plan' });
                        }
                      }}
                    >
                      Buy 1,000 tool calls
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default McpSetup;
